/**
 * Utilitário para busca multi-termo no Supabase
 *
 * Características:
 * - Multi-termo: "rj45 cat6" encontra "rj45 branco cat6"
 * - Case-insensitive: "RJ45" encontra "rj45"
 * - Accent-insensitive: "café" encontra "cafe" e vice-versa
 *
 * Lógica: cada termo deve aparecer em pelo menos um dos campos
 * (AND entre termos, OR entre campos)
 */

// Tipo genérico para query builders do Supabase que suportam .or()
interface SupabaseQueryBuilder<T = unknown> {
  or(filters: string): SupabaseQueryBuilder<T>;
}

/**
 * Remove acentos de um texto
 * "ação" → "acao", "café" → "cafe"
 */
export function removeAccents(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Mapa de caracteres base → classe regex que inclui todas as variantes acentuadas.
 * Usado pelo applyMultiTermSearch para gerar padrões regex accent-insensitive
 * no PostgreSQL via operador ~* (imatch).
 */
const ACCENT_REGEX_CLASS: Record<string, string> = {
  a: '[aáàâãä]',
  e: '[eéèêë]',
  i: '[iíìîï]',
  o: '[oóòôõö]',
  u: '[uúùûü]',
  c: '[cç]',
  n: '[nñ]',
};

/**
 * Converte um termo normalizado (sem acentos, lowercase) em padrão regex
 * que faz match com qualquer variante acentuada.
 *
 * "joao" → "j[oóòôõö][aáàâãä][oóòôõö]"
 * "cafe" → "[cç][aáàâãä]f[eéèêë]"
 *
 * Caracteres especiais de regex são escapados para evitar injeção.
 */
function termToAccentPattern(term: string): string {
  return term
    .split('')
    .map(char => {
      if (ACCENT_REGEX_CLASS[char]) {
        return ACCENT_REGEX_CLASS[char];
      }
      // Escapar caracteres especiais de regex
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('');
}

/**
 * Normaliza texto para busca (lowercase + sem acentos)
 */
export function normalizeForSearch(text: string): string {
  return removeAccents(text.toLowerCase().trim());
}

/**
 * Separa texto em termos de busca normalizados
 */
export function parseSearchTerms(searchText: string | null | undefined): string[] {
  if (!searchText || !searchText.trim()) {
    return [];
  }

  return searchText
    .trim()
    .split(/\s+/)
    .filter(term => term.length > 0)
    .map(term => normalizeForSearch(term));
}

/**
 * Aplica busca multi-termo a uma query Supabase
 *
 * @param query - Query builder do Supabase
 * @param searchText - Texto de busca (ex: "rj45 cat6")
 * @param fields - Campos para buscar (ex: ['nome_fantasia', 'modelo'])
 * @returns Query com filtros aplicados
 *
 * @example
 * let query = supabase.from('produto').select('*');
 * query = applyMultiTermSearch(query, 'rj45 cat6', ['nome_fantasia', 'modelo']);
 */
export function applyMultiTermSearch<T extends SupabaseQueryBuilder>(
  query: T,
  searchText: string | null | undefined,
  fields: string[]
): T {
  if (!searchText || !searchText.trim() || fields.length === 0) {
    return query;
  }

  const terms = parseSearchTerms(searchText);

  if (terms.length === 0) {
    return query;
  }

  // Para cada termo, criar condição OR entre os campos usando regex accent-insensitive.
  // Usa operador ~* (imatch) do PostgreSQL com character classes para cada letra,
  // ex: "joao" → regex "j[oóòôõö][aáàâãä][oóòôõö]" que match "João", "JOAO", etc.
  for (const term of terms) {
    const conditions: string[] = [];
    const pattern = termToAccentPattern(term);

    for (const field of fields) {
      conditions.push(`${field}.imatch.${pattern}`);
    }

    query = query.or(conditions.join(',')) as T;
  }

  return query;
}

/**
 * Verifica se um texto contém todos os termos de busca
 * (case-insensitive e accent-insensitive)
 *
 * @example
 * matchesAllTerms('RJ45 branco CAT6', 'rj45 cat6') // true
 * matchesAllTerms('Ação rápida', 'acao rapida') // true
 */
export function matchesAllTerms(
  text: string | null | undefined,
  searchText: string | null | undefined
): boolean {
  if (!searchText || !searchText.trim()) {
    return true;
  }

  if (!text) {
    return false;
  }

  const normalizedText = normalizeForSearch(text);
  const terms = parseSearchTerms(searchText);

  return terms.every(term => normalizedText.includes(term));
}

/**
 * Verifica se um objeto contém todos os termos em qualquer um dos campos
 * (case-insensitive e accent-insensitive)
 *
 * @example
 * const produto = { nome: 'RJ45 branco', modelo: 'CAT6' };
 * matchesAllTermsInFields(produto, 'rj45 cat6', ['nome', 'modelo']) // true
 */
export function matchesAllTermsInFields(
  obj: Record<string, any> | null | undefined,
  searchText: string | null | undefined,
  fields: string[]
): boolean {
  if (!searchText || !searchText.trim()) {
    return true;
  }

  if (!obj) {
    return false;
  }

  const terms = parseSearchTerms(searchText);

  // Concatenar todos os campos e normalizar
  const combinedText = normalizeForSearch(
    fields
      .map(field => {
        const value = obj[field];
        return typeof value === 'string' ? value : '';
      })
      .join(' ')
  );

  return terms.every(term => combinedText.includes(term));
}

/**
 * Filtra um array usando busca multi-termo
 *
 * @example
 * const produtos = [{ nome: 'RJ45 cat6' }, { nome: 'HDMI' }];
 * filterByMultiTerm(produtos, 'rj45', ['nome']) // [{ nome: 'RJ45 cat6' }]
 */
export function filterByMultiTerm<T extends Record<string, any>>(
  items: T[],
  searchText: string | null | undefined,
  fields: string[]
): T[] {
  if (!searchText || !searchText.trim()) {
    return items;
  }

  return items.filter(item => matchesAllTermsInFields(item, searchText, fields));
}
